import React, { useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Palette, Upload, Save, Image as ImageIcon, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  brand_logo_url: '',
  brand_logo_dark_url: '',
  login_title_text: '',
  login_subtitle_text: '',
  feature_1_title: '',
  feature_1_desc: '',
  feature_2_title: '',
  feature_2_desc: '',
  feature_3_title: '',
  feature_3_desc: '',
  background_image_url: '',
  background_image_dark_url: '',
  background_opacity: 30,
  background_opacity_dark: 30,
  theme_default_light: '#1a1a2e',
  theme_default_dark: '#f59e0b',
  site_title_string: 'Rupiwise',
  favicon_image_url: '',
  login_hero_video_url: '',
  login_hero_poster_url: '',
};

function OpacitySlider({ value, onChange, field, testIdPrefix }) {
  const v = Number(value ?? 30);
  return (
    <div className="pt-3 space-y-2" data-testid={`${testIdPrefix}-row`}>
      <div className="flex items-center justify-between gap-2">
        <Label className="text-slate-300 text-xs uppercase tracking-wider">
          Background image transparency
        </Label>
        <span
          data-testid={`${testIdPrefix}-value`}
          className="text-xs font-mono text-slate-200 px-2 py-0.5 rounded bg-slate-800/70 border border-slate-700"
        >
          {v}%
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={v}
        onChange={(e) => onChange(field, Number(e.target.value))}
        data-testid={`${testIdPrefix}-slider`}
        className="w-full accent-emerald-500"
        aria-label="Background image transparency"
      />
      <p className="text-[11px] text-slate-500">
        Lower = more transparent, higher = more visible. 0% hides the image entirely.
      </p>
    </div>
  );
}

function resolveAssetUrl(url) {
  if (!url) return '';
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  return `${BACKEND_URL}${url}`;
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId} className="space-y-1.5">
      <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
      {children}
    </div>
  );
}

// Favicon + Site Title uploader. Stricter than AssetUploader:
//   - whitelist: .ico / .png / .svg (server enforces matching MIME)
//   - hard 100 KB cap (client + server)
//   - PNG: must be square AND dims ≤ 64×64 (server re-validates with PIL)
// Supports drag-and-drop in addition to click-to-pick.
function FaviconUploader({ value, onUploaded, testId }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const FAV_MAX_BYTES = 100 * 1024;
  const ALLOWED_EXT = ['ico', 'png', 'svg'];

  const validateLocally = (file) => new Promise((resolve, reject) => {
    const ext = (file.name || '').split('.').pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      reject(new Error('Favicon must be a .ico, .png, or .svg file.'));
      return;
    }
    if (file.size > FAV_MAX_BYTES) {
      reject(new Error('Favicon exceeds the maximum file size of 100 KB.'));
      return;
    }
    if (ext !== 'png') {
      // .ico and .svg dimensions can't be inspected via <img/> reliably —
      // trust the server-side check.
      resolve();
      return;
    }
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      if (img.naturalWidth !== img.naturalHeight) {
        reject(new Error(`Favicon must be square (got ${img.naturalWidth}×${img.naturalHeight}).`));
      } else if (img.naturalWidth > 64) {
        reject(new Error(`Favicon dimensions must be ≤ 64×64 px (got ${img.naturalWidth}×${img.naturalHeight}). Use 16×16, 32×32, or 64×64.`));
      } else {
        resolve();
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read favicon image.'));
    };
    img.src = url;
  });

  const upload = async (file) => {
    try {
      await validateLocally(file);
    } catch (err) {
      toast.error(err.message);
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', 'favicon');
    setUploading(true);
    try {
      const res = await adminApi.post('/admin/site-config/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(res.data.url);
      toast.success('Favicon updated');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onPick = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) upload(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) upload(file);
  };

  return (
    <div data-testid={testId} className="space-y-3">
      <div className="flex items-start gap-4">
        <div
          className="w-20 h-20 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden shrink-0"
          data-testid="favicon-preview"
        >
          {value ? (
            <img src={resolveAssetUrl(value)} alt="Favicon" className="w-full h-full object-contain" />
          ) : (
            <ImageIcon className="w-6 h-6 text-slate-600" />
          )}
        </div>

        <label
          htmlFor="favicon-file-input"
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`flex-1 cursor-pointer rounded-lg border-2 border-dashed px-4 py-5 text-center transition-colors ${
            dragOver
              ? 'border-amber-400 bg-amber-500/5'
              : 'border-slate-700 bg-slate-900/50 hover:border-slate-600'
          }`}
          data-testid="favicon-dropzone"
        >
          <Upload className="w-5 h-5 text-slate-400 mx-auto mb-1.5" />
          <div className="text-sm text-slate-200">
            {uploading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…
              </span>
            ) : (
              <>
                <span className="text-amber-400 font-medium">Click to upload</span>
                {' '}or drag and drop
              </>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            .ico, .png, or .svg · ≤ 100 KB · square (16×16, 32×32, or 64×64)
          </p>
          <input
            id="favicon-file-input"
            type="file"
            accept=".ico,.png,.svg,image/x-icon,image/png,image/svg+xml"
            onChange={onPick}
            disabled={uploading}
            className="hidden"
            data-testid="favicon-file-input"
          />
        </label>
      </div>

      {value && (
        <button
          type="button"
          className="text-xs text-slate-400 hover:text-red-400"
          onClick={() => onUploaded('')}
          data-testid="favicon-remove"
        >
          Remove favicon
        </button>
      )}
    </div>
  );
}

function AssetUploader({ label, value, onUploaded, kind, testId, accept = 'image/*', maxBytes = 1024 * 1024, maxLabel = '1MB' }) {
  const [uploading, setUploading] = useState(false);

  const handlePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Client-side size guard — keep this in lockstep with the server cap
    // in `routers/site_config.py::admin_upload_site_asset`.
    if (file.size > maxBytes) {
      toast.error(`File size exceeds maximum limit of ${maxLabel}`);
      e.target.value = '';
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    setUploading(true);
    try {
      const res = await adminApi.post('/admin/site-config/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded(res.data.url);
      toast.success(`${label} updated`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const isVideo = accept.startsWith('video/');

  return (
    <div data-testid={testId} className="flex items-center gap-4">
      <div className="w-20 h-20 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden">
        {value ? (
          isVideo ? (
            <video src={resolveAssetUrl(value)} className="w-full h-full object-cover" muted playsInline preload="metadata" />
          ) : (
            <img src={resolveAssetUrl(value)} alt={label} className="w-full h-full object-contain" />
          )
        ) : (
          <ImageIcon className="w-6 h-6 text-slate-600" />
        )}
      </div>
      <div className="flex-1">
        <Label className="text-slate-300 text-xs uppercase tracking-wider">{label}</Label>
        <div className="flex items-center gap-3 mt-1">
          <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md bg-slate-800 border border-slate-700 text-slate-200 text-sm hover:bg-slate-700">
            {uploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" /> Choose file
              </>
            )}
            <input
              type="file"
              accept={accept}
              onChange={handlePick}
              className="hidden"
              disabled={uploading}
            />
          </label>
          {value && (
            <button
              type="button"
              className="text-xs text-slate-400 hover:text-red-400"
              onClick={() => onUploaded('')}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function AdminBrandingPage() {
  const { admin, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!admin) return;
    adminApi
      .get('/admin/site-config')
      .then((r) => setForm({ ...EMPTY, ...r.data }))
      .catch(() => toast.error('Could not load site configuration'))
      .finally(() => setInitialLoading(false));
  }, [admin]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    const HEX = /^#[0-9a-fA-F]{6}$/;
    if (!HEX.test(form.theme_default_light) || !HEX.test(form.theme_default_dark)) {
      toast.error('Theme defaults must be 6-digit hex values (e.g. #1a1a2e)');
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.put('/admin/site-config', form);
      setForm({ ...EMPTY, ...res.data });
      toast.success('Site branding saved');
      // Tell every live session to re-pull the SuperAdmin defaults so the
      // app-wide ThemeContext re-resolves immediately.
      try { window.dispatchEvent(new Event('site-config-updated')); } catch { /* event dispatch is best-effort */ }
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="admin-branding-page"
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)',
      }}
    >
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800"
              data-testid="admin-branding-back"
            >
              <ArrowLeft className="w-4 h-4 text-slate-300" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
                <Palette className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <div className="text-white font-semibold">Site Branding</div>
                <div className="text-xs text-slate-400">Customize the login page for every tenant</div>
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => window.open('/auth', '_blank')}
            className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
            data-testid="admin-branding-preview"
          >
            Preview login page ↗
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {initialLoading ? (
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading site configuration…
          </div>
        ) : (
          <form onSubmit={handleSave} className="space-y-8">
            {/* Assets */}
            <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-6">
              <h2 className="text-white font-semibold">Brand Assets</h2>

              <div className="space-y-1">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 pb-1">
                  Light mode
                </div>
                <AssetUploader
                  testId="branding-upload-logo"
                  label="Brand Logo (shown on login card)"
                  value={form.brand_logo_url}
                  kind="logo"
                  onUploaded={(url) => set('brand_logo_url', url)}
                />
                <AssetUploader
                  testId="branding-upload-background"
                  label="Background Image (behind login card)"
                  value={form.background_image_url}
                  kind="background"
                  onUploaded={(url) => set('background_image_url', url)}
                />
                <OpacitySlider
                  value={form.background_opacity}
                  field="background_opacity"
                  onChange={set}
                  testIdPrefix="branding-bg-opacity"
                />
              </div>

              <div className="space-y-1 pt-4 border-t border-slate-800">
                <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 pb-1 flex items-center gap-1.5">
                  <span>Dark mode</span>
                  <span className="text-[10px] font-normal text-slate-500 normal-case tracking-normal">
                    — falls back to the light asset when not provided
                  </span>
                </div>
                <AssetUploader
                  testId="branding-upload-logo-dark"
                  label="Brand Logo (dark mode)"
                  value={form.brand_logo_dark_url}
                  kind="logo_dark"
                  onUploaded={(url) => set('brand_logo_dark_url', url)}
                />
                <AssetUploader
                  testId="branding-upload-background-dark"
                  label="Background Image (dark mode)"
                  value={form.background_image_dark_url}
                  kind="background_dark"
                  onUploaded={(url) => set('background_image_dark_url', url)}
                />
                <OpacitySlider
                  value={form.background_opacity_dark}
                  field="background_opacity_dark"
                  onChange={set}
                  testIdPrefix="branding-bg-opacity-dark"
                />
              </div>
            </section>

            {/* Login-page hero (left panel) — SuperAdmin-swappable video
                + poster. Falls back to `/brand/auth-hero.mp4` +
                `/brand/auth-hero-poster.jpg` shipped in the repo when
                blank. See `AuthPage.js` for the render path. */}
            <section
              data-testid="branding-hero"
              className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-5"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-white font-semibold">Login Page Hero</h2>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                  AuthPage · Left panel
                </span>
              </div>
              <p className="text-xs text-slate-400 -mt-2">
                Ambient looping video shown behind the feature-highlight rows on the login page.
                The poster is painted first so the panel isn't dark during the ~200 ms MP4 buffer.
                Both fall back to the repo-shipped defaults when cleared.
              </p>
              <AssetUploader
                testId="branding-upload-hero-video"
                label="Hero video (.mp4 / .webm · max 10 MB)"
                value={form.login_hero_video_url}
                kind="login_hero_video"
                accept="video/mp4,video/webm"
                maxBytes={10 * 1024 * 1024}
                maxLabel="10MB"
                onUploaded={(url) => set('login_hero_video_url', url)}
              />
              <AssetUploader
                testId="branding-upload-hero-poster"
                label="Poster frame (image · max 1 MB)"
                value={form.login_hero_poster_url}
                kind="login_hero_poster"
                onUploaded={(url) => set('login_hero_poster_url', url)}
              />
            </section>

            {/* Browser Tab & Favicon — drives <title> and <link rel="icon">
                across every route. Pulled by `useBrowserMetadata` on app
                mount and re-applied on `site-config-updated`. */}
            <section
              data-testid="branding-browser-meta"
              className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-5"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-white font-semibold">Browser Tab &amp; Favicon</h2>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                  Live across all routes
                </span>
              </div>
              <p className="text-xs text-slate-400 -mt-2">
                These values override the static <code className="text-slate-300">&lt;title&gt;</code> and
                {' '}<code className="text-slate-300">&lt;link rel=&quot;icon&quot;&gt;</code> in the SPA shell on every page.
              </p>

              <Field label="Favicon image" testId="branding-field-favicon">
                <FaviconUploader
                  testId="branding-upload-favicon"
                  value={form.favicon_image_url}
                  onUploaded={(url) => set('favicon_image_url', url)}
                />
              </Field>

              <Field label="Site title (browser tab)" testId="branding-field-site-title">
                <Input
                  value={form.site_title_string}
                  onChange={(e) => set('site_title_string', e.target.value)}
                  className="bg-slate-800/50 border-slate-700 text-white"
                  maxLength={60}
                  placeholder="Rupiwise"
                  data-testid="branding-site-title-input"
                />
                <div className="flex items-center justify-between mt-1">
                  <p className="text-[11px] text-slate-500">
                    Shown in the browser tab. Capped at 60 chars for SEO.
                  </p>
                  <span
                    className="text-[11px] font-mono text-slate-500"
                    data-testid="branding-site-title-counter"
                  >
                    {(form.site_title_string || '').length} / 60
                  </span>
                </div>
                <div
                  className="mt-2 flex items-start gap-2 rounded-md border border-slate-700/60 bg-slate-800/40 px-3 py-2"
                  data-testid="branding-site-title-multitenant-note"
                >
                  <Info className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <p className="text-[11px] leading-relaxed text-slate-400">
                    <span className="font-semibold text-slate-300">Note:</span>{' '}
                    The active tenant&apos;s Company Name will be automatically appended to your baseline site title within authenticated user workspaces{' '}
                    <span className="font-mono text-slate-300">
                      (e.g., Baseline Title | Company Name)
                    </span>
                    .
                  </p>
                </div>
              </Field>
            </section>

            {/* Title */}
            <section className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-4">
              <h2 className="text-white font-semibold">Login Card</h2>
              <div className="grid grid-cols-1 gap-4">
                <Field label="Subtitle text" testId="branding-field-subtitle">
                  <Input
                    value={form.login_subtitle_text}
                    onChange={(e) => set('login_subtitle_text', e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-white"
                    maxLength={120}
                    placeholder="Welcome back"
                  />
                </Field>
              </div>
            </section>

            {/* Theme Defaults — controls the paired sentinel pair used app-wide.
                Pro orgs may override these per-user; Standard orgs are locked
                to whichever values are configured here. Both must be valid
                6-digit hex strings (validated server-side). */}
            <section
              data-testid="branding-theme-defaults"
              className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-4"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-white font-semibold">App Theme Defaults</h2>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                  System paired sentinels
                </span>
              </div>
              <p className="text-xs text-slate-400 -mt-1">
                Define the default app theme color for each mode. Standard-tier users
                are locked to these; Pro users get them as the &quot;Reset to default&quot;
                target and as the paired sentinel for auto-flip on mode change.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { label: 'Light mode default', field: 'theme_default_light' },
                  { label: 'Dark mode default', field: 'theme_default_dark' },
                ].map((cfg) => (
                  <div
                    key={cfg.field}
                    className="space-y-2"
                    data-testid={`branding-${cfg.field}-row`}
                  >
                    <Label className="text-slate-300 text-xs uppercase tracking-wider">
                      {cfg.label}
                    </Label>
                    <div className="flex items-center gap-3">
                      {/* Native color input — gives a real OS color picker on
                          every browser. We mirror the value into the hex text
                          field so power users can paste an exact hex. */}
                      <input
                        type="color"
                        value={form[cfg.field] || '#000000'}
                        onChange={(e) => set(cfg.field, e.target.value)}
                        className="h-10 w-14 rounded border border-slate-700 bg-slate-800 cursor-pointer"
                        data-testid={`branding-${cfg.field}-color`}
                        aria-label={`${cfg.label} color`}
                      />
                      <Input
                        value={form[cfg.field]}
                        onChange={(e) => set(cfg.field, e.target.value)}
                        placeholder="#1a1a2e"
                        maxLength={7}
                        className="bg-slate-800/50 border-slate-700 text-white font-mono uppercase flex-1"
                        data-testid={`branding-${cfg.field}-hex`}
                      />
                    </div>
                    <div
                      className="h-2 rounded-full"
                      style={{ backgroundColor: form[cfg.field] || '#1a1a2e' }}
                    />
                  </div>
                ))}
              </div>
            </section>

            {/* Features */}
            {[1, 2, 3].map((n) => (
              <section
                key={n}
                data-testid={`branding-feature-${n}`}
                className="bg-slate-900/70 border border-slate-800 rounded-2xl p-6 space-y-4"
              >
                <h2 className="text-white font-semibold">Feature #{n}</h2>
                <Field label="Title">
                  <Input
                    value={form[`feature_${n}_title`]}
                    onChange={(e) => set(`feature_${n}_title`, e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-white"
                    maxLength={80}
                  />
                </Field>
                <Field label="Description">
                  <Textarea
                    value={form[`feature_${n}_desc`]}
                    onChange={(e) => set(`feature_${n}_desc`, e.target.value)}
                    className="bg-slate-800/50 border-slate-700 text-white"
                    maxLength={240}
                    rows={2}
                  />
                </Field>
              </section>
            ))}

            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin')}
                className="border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800 hover:text-white"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="admin-branding-save"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" /> Save branding
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
